import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, X } from 'lucide-react';
import type { ProjectDetails } from '@/types/project';
import type { PaginationInfo } from '@/services/projectsApi';
import type { DataMode } from '@/contexts/DataSourceContext';

interface ProjectFilterBarProps {
  searchQuery: string;
  statusFilter: string;
  priorityFilter: string;
  sortBy: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusFilterChange: (value: string) => void;
  onPriorityFilterChange: (value: string) => void;
  onSortByChange: (value: string) => void;
  onClearSearch: () => void;
  onClearAllFilters: () => void;
  hasActiveFilters: boolean;
  projectIdFilter: string | null;
  onClearProjectIdFilter: () => void;
  displayProjects: ProjectDetails[];
  screenMode: DataMode;
  pagination: PaginationInfo;
  totalMockProjects: number;
}

export const ProjectFilterBar: React.FC<ProjectFilterBarProps> = ({
  searchQuery,
  statusFilter,
  priorityFilter,
  sortBy,
  onSearchChange,
  onStatusFilterChange,
  onPriorityFilterChange,
  onSortByChange,
  onClearSearch,
  onClearAllFilters,
  hasActiveFilters,
  projectIdFilter,
  onClearProjectIdFilter,
  displayProjects,
  screenMode,
  pagination,
  totalMockProjects,
}) => {
  const filteredProject = projectIdFilter
    ? displayProjects.find(p => p.id === projectIdFilter)
    : undefined;

  return (
    <Card data-tour="projects-filter">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Search & Filter</CardTitle>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAllFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {/* Enhanced Search with Icon and Clear Button */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <Input
                placeholder="Search projects, codes, or clients..."
                value={searchQuery}
                onChange={onSearchChange}
                className="pl-9 pr-9"
                aria-label="Search projects"
                disabled={projectIdFilter !== null}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearSearch}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </Button>
              )}
            </div>
          </div>

          <Select value={statusFilter} onValueChange={onStatusFilterChange} disabled={projectIdFilter !== null}>
            <SelectTrigger aria-label="Filter by status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="at-risk">At Risk</SelectItem>
              <SelectItem value="on-hold">On Hold</SelectItem>
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={onPriorityFilterChange} disabled={projectIdFilter !== null}>
            <SelectTrigger aria-label="Filter by priority">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={onSortByChange} disabled={projectIdFilter !== null}>
            <SelectTrigger aria-label="Sort projects">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Project Name</SelectItem>
              <SelectItem value="code">Project Code</SelectItem>
              <SelectItem value="client">Client Name</SelectItem>
              <SelectItem value="startDate">Start Date</SelectItem>
              <SelectItem value="endDate">End Date</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Active filters:
            </span>
            {projectIdFilter && (
              <Badge variant="default" className="gap-1 bg-primary">
                Project: {filteredProject?.name || projectIdFilter} - {filteredProject?.code || 'N/A'}
                <button
                  onClick={onClearProjectIdFilter}
                  className="ml-1 hover:bg-primary/80 rounded-full"
                  aria-label="Remove project filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {searchQuery && (
              <Badge variant="secondary" className="gap-1">
                Search: {searchQuery}
                <button
                  onClick={onClearSearch}
                  className="ml-1 hover:bg-muted rounded-full"
                  aria-label="Remove search filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {statusFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1">
                Status: {statusFilter}
                <button
                  onClick={() => onStatusFilterChange('all')}
                  className="ml-1 hover:bg-muted rounded-full"
                  aria-label="Remove status filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {priorityFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1">
                Priority: {priorityFilter}
                <button
                  onClick={() => onPriorityFilterChange('all')}
                  className="ml-1 hover:bg-muted rounded-full"
                  aria-label="Remove priority filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {(hasActiveFilters || screenMode === 'live') && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {screenMode === 'live'
                ? `Showing ${displayProjects.length} of ${pagination.totalItems} projects`
                : `Showing ${displayProjects.length} of ${totalMockProjects} projects`
              }
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
